
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crown, Zap, Star, Check, Loader2, Infinity } from "lucide-react";
import { useSubscriptionUpgrade } from "@/hooks/useSubscriptionUpgrade";
import { getSubscriptionLimits, formatProfileLimit, getSubscriptionDisplayName, getSubscriptionPrice, type SubscriptionTier } from "@/utils/subscriptionUtils";

interface SubscriptionLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier: SubscriptionTier;
  profilesViewed: number;
  profileLimit: number;
}

const SubscriptionLimitModal = ({ 
  isOpen, 
  onClose, 
  currentTier, 
  profilesViewed, 
  profileLimit 
}: SubscriptionLimitModalProps) => {
  
  const { upgradeSubscription, isUpgrading } = useSubscriptionUpgrade();

  const plans = [
    {
      name: 'Alpha200',
      tier: 'alpha200' as const,
      price: 'Free',
      profileLimit: 200,
      icon: Star,
      color: 'bg-stellar-cyan',
      gradient: 'from-stellar-cyan to-cosmic-pink',
      features: [
        '200 profiles per day',
        'Basic space filters',
        'Standard cosmic support',
        'Essential discovery features'
      ]
    },
    {
      name: 'Alpha600',
      tier: 'alpha600' as const,
      price: '$14.99/month',
      profileLimit: 600,
      icon: Crown,
      color: 'bg-cosmic-pink',
      gradient: 'from-cosmic-pink to-stellar-purple',
      features: [
        '600 profiles per day',
        'Advanced space filters',
        'Who viewed your profile',
        'Priority cosmic support',
        'Read receipts',
        'Enhanced discovery'
      ]
    },
    {
      name: 'Alpha∞',
      tier: 'alpha_unlimited' as const,
      price: '$29.99/month',
      profileLimit: 999,
      icon: Infinity,
      color: 'bg-stellar-purple',
      gradient: 'from-stellar-purple to-cosmic-gold',
      features: [
        'Unlimited profiles',
        'All Alpha600 features',
        'Priority placement',
        'Advanced privacy controls',
        'Incognito browsing',
        '24/7 VIP support',
        'Exclusive Alpha perks'
      ]
    }
  ];

  const handleUpgrade = async (targetTier: SubscriptionTier) => {
    const result = await upgradeSubscription(targetTier);
    if (result.success) {
      // Close modal after successful upgrade
      setTimeout(() => {
        onClose();
        // Refresh the page to update subscription status
        window.location.reload();
      }, 1500);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto space-pod border-stellar-cyan/50">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold holographic-text">
            Alpha Limit Reached
          </DialogTitle>
          <p className="text-center text-white/70">
            You've explored {profilesViewed} of {profileLimit} daily profiles. Upgrade your Alpha status for more cosmic connections!
          </p>
        </DialogHeader>

        <div className="grid gap-4 mt-6">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = currentTier === plan.tier;
            const isUpgrade = plans.findIndex(p => p.tier === currentTier) < plans.findIndex(p => p.tier === plan.tier);
            
            return (
              <div
                key={plan.tier}
                className={`relative p-6 rounded-2xl border-2 transition-all backdrop-blur-md ${
                  isCurrentPlan 
                    ? 'border-cosmic-pink bg-cosmic-pink/10' 
                    : 'border-white/20 hover:border-white/40 bg-black/20'
                }`}
              >
                {isCurrentPlan && (
                  <Badge className="absolute -top-2 left-4 bg-cosmic-pink border-cosmic-pink text-white">
                    Current Alpha Status
                  </Badge>
                )}
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-full ${plan.color} text-white shadow-stellar-cyan`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-white holographic-text">{plan.name}</h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-stellar-cyan to-cosmic-pink bg-clip-text text-transparent">
                        {plan.price}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm text-white/60">Daily Profiles</p>
                    <p className="text-3xl font-bold text-white">
                      {formatProfileLimit(plan.profileLimit)}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="w-4 h-4 text-stellar-cyan" />
                      <span className="text-sm text-white/80">{feature}</span>
                    </div>
                  ))}
                </div>

                {!isCurrentPlan && isUpgrade && (
                  <Button
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={isUpgrading}
                    className={`w-full cosmic-button bg-gradient-to-r ${plan.gradient} hover:opacity-90 text-white font-semibold py-3`}
                  >
                    {isUpgrading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Upgrading Alpha Status...
                      </>
                    ) : (
                      `Upgrade to ${plan.name}`
                    )}
                  </Button>
                )}
                
                {isCurrentPlan && (
                  <Button variant="outline" className="w-full border-cosmic-pink text-cosmic-pink" disabled>
                    Current Alpha Status
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-stellar-cyan/10 to-cosmic-pink/10 rounded-xl border border-stellar-cyan/30">
          <p className="text-sm text-white/80">
            <strong className="text-stellar-cyan">Why upgrade your Alpha status?</strong> Explore more cosmic profiles, unlock advanced features, and find your perfect space match faster than ever!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionLimitModal;
